import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState
} from 'react';
import { Language } from '../../generated/types';
import { useSetUserLanguageMutation } from './mutations.generated';
import { useGetUserLanguageQuery } from './queries.generated';
import dayjs from 'dayjs';

export const LocaleContext = createContext<{
  locale: Language;
  setLocale: (l: Language) => void;
}>({
  locale: Language.En,
  setLocale: (_l: Language): void => {
    /* Do nothing */
  }
});

export function LocaleProvider({
  children
}: PropsWithChildren<unknown>): JSX.Element {
  const { data } = useGetUserLanguageQuery();
  const [setUserLanguage] = useSetUserLanguageMutation();
  const [locale, setLocale] = useState<Language>(Language.En);

  useEffect(() => {
    dayjs.locale(locale);
  }, [locale]);

  useEffect(() => {
    if (
      typeof data !== 'undefined' &&
      Object.values(Language).includes(data.user.language)
    ) {
      setLocale(data.user.language);
    }
  }, [data]);

  const updateAndPersistLocale = useCallback(
    (language: Language) => {
      (async (): Promise<void> => {
        const res = await setUserLanguage({
          variables: { language }
        });

        if (
          Object.values(Language).includes(
            res.data?.setUserLanguage.language as Language
          )
        ) {
          setLocale(res.data?.setUserLanguage.language as Language);
        }
      })();
    },
    [setUserLanguage]
  );

  return (
    <LocaleContext.Provider
      value={{
        locale,
        setLocale: updateAndPersistLocale
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
}
